<script>
import PartnerEdit from '@/components/page-partners/PartnerEdit.vue';
import PartnerLinkList from '@/components/page-partners/PartnerLinkList.vue';
import PartnerCohortStats from '@/components/page-partners/PartnerCohortStats.vue';
import PartnerAggregateFunnel from '@/components/page-partners/PartnerAggregateFunnel.vue';
import Button from 'primevue/button';
import Tag from 'primevue/tag';

export default {
    name: 'PartnerObject',

    components: {
        PartnerEdit,
        PartnerLinkList,
        PartnerCohortStats,
        PartnerAggregateFunnel,
        Button,
        Tag,
    },

    props: {
        partner: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            activeTab: null, // null | 'edit' | 'links' | 'stats'
            showActionModal: false,
            actionModalMode: null, // 'deactivate' | 'activate'
            isActioning: false,
        };
    },

    methods: {
        async setTab(tab) {
            if (this.activeTab === tab) {
                this.activeTab = null;
                return;
            }
            this.activeTab = tab;
            if (tab === 'links') {
                await this.$store.dispatch('loadPartnerLinks', this.partner.id);
                this.$store.commit('SET_SELECTED_PARTNER', this.partner);
            }
        },

        async confirmDelete() {
            this.actionModalMode = 'deactivate';
            this.showActionModal = true;
        },

        confirmActivate() {
            this.actionModalMode = 'activate';
            this.showActionModal = true;
        },

        async executeAction() {
            this.isActioning = true;
            const isActivating = this.actionModalMode === 'activate';
            console.debug('[PartnerObject] action', this.actionModalMode, 'partner', this.partner.id, this.partner.name);
            try {
                if (isActivating) {
                    await this.$store.dispatch('togglePartnerActive', {
                        partnerId: this.partner.id,
                        isActive: true,
                    });
                    console.debug('[PartnerObject] partner activated', this.partner.id);
                } else {
                    await this.$store.dispatch('removePartner', this.partner.id);
                    console.debug('[PartnerObject] partner deactivated', this.partner.id);
                }
            } catch (e) {
                console.error('[PartnerObject] action failed', this.actionModalMode, this.partner.id, e);
            } finally {
                this.isActioning = false;
                this.showActionModal = false;
                this.actionModalMode = null;
            }
        },

        cancelAction() {
            this.showActionModal = false;
            this.actionModalMode = null;
        },

        onSaved() {
            this.activeTab = null;
        },

        openLinksTab() {
            this.setTab('links');
        },
    },
};
</script>

<template>
    <div class="partner-card" :class="{ 'partner-card--inactive': !partner.is_active }">
        <!-- Row -->
        <div class="partner-row">
            <div class="partner-col partner-col--name">
                <span class="partner-name">{{ partner.name }}</span>
                <span class="partner-code">{{ partner.code }}</span>
            </div>
            <div class="partner-col partner-col--type">
                <Tag v-if="partner.source_type" :value="partner.source_type" severity="secondary" />
                <span v-else class="type-empty">—</span>
            </div>
            <div class="partner-col partner-col--status">
                <span :class="['status-dot', partner.is_active ? 'status-dot--active' : 'status-dot--inactive']">
                    {{ partner.is_active ? 'Active' : 'Inactive' }}
                </span>
            </div>
            <div class="partner-col partner-col--actions">
                <Button
                    label="Edit"
                    size="small"
                    :severity="activeTab === 'edit' ? 'primary' : 'secondary'"
                    :outlined="activeTab !== 'edit'"
                    @click="setTab('edit')"
                />
                <Button
                    label="Links"
                    size="small"
                    :severity="activeTab === 'links' ? 'primary' : 'secondary'"
                    :outlined="activeTab !== 'links'"
                    @click="setTab('links')"
                />
                <Button
                    label="Stats"
                    size="small"
                    :severity="activeTab === 'stats' ? 'primary' : 'secondary'"
                    :outlined="activeTab !== 'stats'"
                    @click="setTab('stats')"
                />
                <Button
                    v-if="partner.is_active"
                    label="Deactivate"
                    size="small"
                    severity="danger"
                    outlined
                    @click="confirmDelete"
                />
                <Button
                    v-else
                    label="Activate"
                    size="small"
                    severity="success"
                    outlined
                    @click="confirmActivate"
                />
            </div>
        </div>

        <!-- Expanded panel -->
        <div v-if="activeTab" class="partner-detail">
            <PartnerEdit
                v-if="activeTab === 'edit'"
                :partner="partner"
                @saved="onSaved"
                @cancel="activeTab = null"
            />
            <PartnerLinkList
                v-else-if="activeTab === 'links'"
                :partner-id="partner.id"
            />
            <template v-else-if="activeTab === 'stats'">
                <PartnerAggregateFunnel :partner-id="partner.id" />
                <PartnerCohortStats :partner-id="partner.id" />
            </template>
        </div>

        <!-- Action confirmation modal (deactivate / activate) -->
        <Teleport to="body">
            <div v-if="showActionModal" class="del-overlay" @click.self="cancelAction">
                <div class="del-modal" role="dialog" aria-modal="true">
                    <div class="del-modal__icon">{{ actionModalMode === 'activate' ? '✅' : '⏸' }}</div>
                    <div class="del-modal__title">
                        {{ actionModalMode === 'activate' ? 'Activate partner?' : 'Deactivate partner?' }}
                    </div>
                    <div class="del-modal__body">
                        <template v-if="actionModalMode === 'activate'">
                            <strong>{{ partner.name }}</strong> will become active again.
                            Their tracking links will start recording traffic.
                        </template>
                        <template v-else>
                            <strong>{{ partner.name }}</strong> will be deactivated.
                            Their tracking links will remain intact but the partner will be marked inactive.
                        </template>
                    </div>
                    <div class="del-modal__actions">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            :disabled="isActioning"
                            @click="cancelAction"
                        />
                        <Button
                            :label="actionModalMode === 'activate' ? 'Activate' : 'Deactivate'"
                            :severity="actionModalMode === 'activate' ? 'success' : 'danger'"
                            :loading="isActioning"
                            @click="executeAction"
                        />
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.partner-card {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 8px;
    background: #fff;
    overflow: hidden;
    transition: box-shadow 0.15s;
}
.partner-card:hover {
    box-shadow: 0 1px 6px rgba(0,0,0,0.07);
}
.partner-card--inactive {
    opacity: 0.6;
}
.partner-row {
    display: grid;
    grid-template-columns: 1fr 140px 100px auto;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
}
.partner-col--name {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.partner-name {
    font-weight: 600;
    font-size: 0.9rem;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.partner-code {
    font-family: monospace;
    font-size: 0.75rem;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.type-badge {
    display: inline-block;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 0.75rem;
    color: #475569;
    white-space: nowrap;
}
.type-empty {
    color: #cbd5e1;
    font-size: 0.85rem;
}
.status-dot {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.78rem;
    font-weight: 500;
}
.status-dot--active {
    color: #16a34a;
}
.status-dot--active::before {
    content: '';
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22c55e;
}
.status-dot--inactive {
    color: #94a3b8;
}
.status-dot--inactive::before {
    content: '';
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #cbd5e1;
}
.partner-col--actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    flex-wrap: nowrap;
}
.tab-btn {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    padding: 4px 10px;
    font-size: 0.78rem;
    cursor: pointer;
    color: #475569;
    white-space: nowrap;
    transition: background 0.12s, border-color 0.12s;
}
.tab-btn:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
}
.tab-btn--active {
    background: #f0fdf4;
    border-color: #86efac;
    color: #16a34a;
}
.tab-btn--danger {
    color: #dc2626;
    border-color: #fecaca;
    background: #fff5f5;
}
.tab-btn--danger:hover {
    background: #fee2e2;
    border-color: #fca5a5;
}
.partner-detail {
    border-top: 1px solid #f1f5f9;
    background: #fafbfc;
    padding: 16px;
}
</style>

<style>
/* Non-scoped: modal is teleported to body */
.del-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
}
.del-modal {
    background: #fff;
    border-radius: 10px;
    padding: 28px 28px 24px;
    width: 380px;
    max-width: calc(100vw - 32px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    text-align: center;
}
.del-modal__icon {
    font-size: 28px;
    margin-bottom: 10px;
}
.del-modal__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 8px;
}
.del-modal__body {
    font-size: 0.875rem;
    color: #475569;
    line-height: 1.55;
    margin-bottom: 22px;
}
.del-modal__actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
}
</style>
